/* eslint-disable max-len */
// Sets data-open on the clean-coded primary nav's dropdown triggers
// (.b3rg-nav) on hover/focus — and that attribute IS the dropdown's open
// state, not just a mirror of it: _nav-mega-clean.scss shows/hides the panel
// off `[data-open="true"]` directly (a single source of truth for the visual
// state, instead of :hover/:focus-within driving the visuals independently).
// aria-expanded is set alongside it in lockstep, purely for screen readers —
// it's no longer what JS/CSS read to decide state. The close-delay grace
// period and diagonal-hover tolerance are still pure CSS (asymmetric
// transition-delay, flush trigger-to-panel positioning) — only the
// open/closed boolean itself comes from here. This does mean the dropdown
// needs this script to open at all; there's no independent CSS-only
// fallback if it fails to load. See docs/navigation-clean-rebuild.md,
// "Move interactivity to CSS".
document.addEventListener( 'DOMContentLoaded', function () {
  const nav = document.querySelector( '.b3rg-nav' );
  if ( !nav ) return;

  // Below this width the dropdown is an accordion section inside the
  // off-canvas panel (nav-clean-mobile.js), not a hover-opened overlay —
  // matches the $grid-breakpoints xl value _header-mobile.scss's own
  // media-breakpoint-down(xl) mobile nav compiles to.
  const mobileQuery = window.matchMedia( '(max-width: 1199.98px)' );

  // Resources and Services are wide enough, and sit far enough
  // right-of-center in the nav, that centering them under their own
  // trigger (every other dropdown's behavior) either overflowed the
  // viewport or read as visibly off-center — _nav-mega-clean.scss makes
  // these two position: fixed (centered on the viewport itself instead),
  // which means top: 100% (meaningful for position: absolute, relative to
  // the trigger) no longer means anything useful, since percentage insets
  // on a fixed element resolve against the viewport, not the trigger. This
  // sets the real value: the trigger's own bottom edge, in viewport
  // coordinates (which is exactly what getBoundingClientRect() already
  // returns, so no further conversion is needed).
  function positionFixedDropdown( link, dropdown ) {
    dropdown.style.top = link.getBoundingClientRect().bottom + 'px';
  }

  // Keeps a dropdown on-screen when centering it (under its trigger for
  // most items, on the viewport for Resources/Services above) would push
  // it past the viewport edge at narrower desktop widths. No single fixed
  // CSS anchor (centered / left: 0 / right: 0) avoids overflow at every
  // viewport width, so the correction is measured directly instead. Safe
  // to call regardless of open state — getBoundingClientRect() reflects
  // layout position even while the dropdown is opacity: 0/visibility:
  // hidden.
  function correctDropdownOverflow( dropdown ) {
    dropdown.style.removeProperty( '--b3rg-dropdown-shift' );
    const rect = dropdown.getBoundingClientRect();
    const margin = 16;
    const overflowRight = rect.right - ( window.innerWidth - margin );
    const overflowLeft = margin - rect.left;
    let shift = 0;
    if ( overflowRight > 0 && overflowLeft > 0 ) {
      // Wider than the viewport can fit with margin on both sides at all
      // (e.g. right at the 1200px desktop/mobile boundary) — no shift
      // satisfies both edges, so split the difference rather than fully
      // solving one side and pushing the other further past its edge.
      shift = ( overflowLeft - overflowRight ) / 2;
    } else if ( overflowRight > 0 ) {
      shift = -overflowRight;
    } else if ( overflowLeft > 0 ) {
      shift = overflowLeft;
    }
    if ( shift !== 0 ) {
      dropdown.style.setProperty( '--b3rg-dropdown-shift', shift + 'px' );
    }
  }

  const items = nav.querySelectorAll( '.b3rg-nav__item--has-dropdown' );

  items.forEach( function ( item ) {
    const link = item.querySelector( ':scope > .b3rg-nav__top-link' );
    const dropdown = link ? link.nextElementSibling : null;
    if ( !link || !dropdown ) return;

    // Matches _nav-mega-clean.scss's position: fixed override for these
    // two specific dropdowns (see positionFixedDropdown() above).
    const isFixedDropdown = item.classList.contains( 'nav-card--resources' ) || item.classList.contains( 'nav-card--services' );

    // "Open" = hovering OR focus is within — either alone should keep the
    // dropdown up (e.g. tabbing in without the mouse over it).
    let hovering = false;
    let focusedWithin = false;

    function sync() {
      const open = hovering || focusedWithin;
      link.setAttribute( 'data-open', open ? 'true' : 'false' );
      link.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
      if ( isFixedDropdown ) positionFixedDropdown( link, dropdown );
      correctDropdownOverflow( dropdown );
    }

    item.addEventListener( 'mouseenter', function () {
      if ( mobileQuery.matches ) return;
      hovering = true;
      sync();
    } );
    item.addEventListener( 'mouseleave', function () {
      if ( mobileQuery.matches ) return;
      hovering = false;
      sync();
    } );
    item.addEventListener( 'focusin', function () {
      if ( mobileQuery.matches ) return;
      focusedWithin = true;
      sync();
    } );
    item.addEventListener( 'focusout', function () {
      if ( mobileQuery.matches ) return;
      focusedWithin = false;
      sync();
    } );

    // Mobile: tap the trigger to toggle its accordion section instead of
    // hovering. preventDefault() is safe here — these triggers render with
    // an empty href (see Walker_Nav_Menu_Mega::renderTopLevelItem()), so
    // there's no real destination being blocked.
    link.addEventListener( 'click', function ( event ) {
      if ( !mobileQuery.matches ) return;
      event.preventDefault();
      const open = link.getAttribute( 'data-open' ) !== 'true';
      link.setAttribute( 'data-open', open ? 'true' : 'false' );
      link.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
    } );

    // Re-measure on resize — the overflow amount (and so the correction
    // needed) depends on viewport width, not just whether this is open.
    window.addEventListener( 'resize', function () {
      if ( link.getAttribute( 'data-open' ) !== 'true' ) return;
      if ( isFixedDropdown ) positionFixedDropdown( link, dropdown );
      correctDropdownOverflow( dropdown );
    } );
  } );
} );
